import { ErrorModal, MesfModal, useUserContext } from "@dexteel/mesf-core";
import {
	Checkbox,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Grid2,
	TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";

import {
	CATALOG_OPERATOR_INITIAL_VALUES,
	CatalogOperator,
} from "@/pages/configuration/models/CatalogOperator";
import { HelmetDexteel } from "@dexteel/mesf-core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	deleteOperator,
	upsertOperator,
} from "../../../repositories/CatalogOperatorRepository";

type Props = {
	operator: CatalogOperator | null;
	modal: "create" | "update" | "delete" | "";
	onHide: (shouldUpdate: boolean) => void;
};

export const UpsertDeleteModal = ({ operator, modal, onHide }: Props) => {
	const {
		state: { defaultAreaId },
	} = useUserContext();
	const [title, setTitle] = useState<string>("CREATE");
	const [message, setNotificationMessage] = useState<string>("");
	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);

	const succesfullyMessage = `The operator was ${modal}d succesfully`;

	const { control, setValue, reset, handleSubmit } = useForm<CatalogOperator>({
		defaultValues: CATALOG_OPERATOR_INITIAL_VALUES,
	});

	const queryClient = useQueryClient();
	const deleteMutation = useMutation(deleteOperator, {
		onSuccess: async () => {
			await queryClient.invalidateQueries(["operators"]);
			setNotificationMessage(succesfullyMessage);
			onHide(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});
	const upsertMutation = useMutation(upsertOperator, {
		onSuccess: async () => {
			await queryClient.invalidateQueries(["operators"]);
			setNotificationMessage(succesfullyMessage);
			onHide(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	const onSubmit: SubmitHandler<CatalogOperator> = async (data) => {
		setIsSubmitLoading(true);
		if (modal === "delete") {
			deleteMutation.mutate({
				operatorId: operator?.OperatorId ?? null,
				defaultAreaId,
			});
		} else {
			upsertMutation.mutate({ operator: data, defaultAreaId });
		}
	};

	useEffect(() => {
		if (modal !== "") {
			setIsSubmitLoading(false);
			reset(
				modal === "create"
					? CATALOG_OPERATOR_INITIAL_VALUES
					: operator || CATALOG_OPERATOR_INITIAL_VALUES,
			);
			setTitle(
				modal === "create" ? "CREATE" : modal === "update" ? "EDIT" : "DELETE",
			);
		}
	}, [modal, operator]);

	return (
		<>
			{(modal === "create" || modal === "update" || modal === "delete") && (
				<HelmetDexteel
					title={`${
						operator === null
							? "New"
							: `${modal === "update" ? "Edit" : "Delete"}`
					} Operator - Operators`}
				/>
			)}
			<MesfModal
				title={`${title} OPERATOR`}
				open={modal !== ""}
				handleClose={() => onHide(false)}
				maxWidth="sm"
				id="operator-modal"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers style={{ padding: "10px 30px" }}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12 }}>
								<Controller
									name="OperatorName"
									control={control}
									rules={{ required: "Name is required" }}
									render={({
										field: { onChange, value },
										fieldState: { error },
									}) => (
										<FormControl error={!!error} fullWidth>
											<TextField
												label="Name"
												variant="outlined"
												fullWidth
												margin="dense"
												autoComplete="off"
												value={value}
												disabled={modal === "delete"}
												onChange={onChange}
											/>
											{error && (
												<FormHelperText>{error.message}</FormHelperText>
											)}
										</FormControl>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, sm: 12, md: 4 }}>
								<Controller
									name="Enabled"
									control={control}
									render={({ field }) => (
										<FormControlLabel
											control={
												<Checkbox
													checked={field.value}
													onChange={field.onChange}
													name="Enabled"
													color="primary"
													disabled={modal === "delete"}
												/>
											}
											label="Enabled"
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<FooterModalButtons
							isSubmitLoading={isSubmitLoading}
							onHide={onHide}
							buttonLabel={modal === "delete" ? "Delete" : "Save"}
							buttonColor={modal === "delete" ? "secondary" : "primary"}
						/>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<NotificationSnackBar
				message={message}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error In Operator"
			/>
		</>
	);
};
